import React, { useEffect, useRef, useState } from 'react'
import {
  Image,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from 'react-native'
import * as DocumentPicker from 'expo-document-picker'
import * as ImageManipulator from 'expo-image-manipulator'
import Card from '@components/Card'
import Button from '@components/Button'
import ImageThumbnail from '@components/ImageThumbnail'
import { colors } from '@lib/colors'
import { i18n } from '@lib/i18n'
import runAsync from '@lib/utils/runAsync'
import Header from '@components/Header'
import ScrollContainer from '@components/ScrollContainer'
import StepDescription from '@components/StepDescription'
import Show from '@components/Show'
import ImagePreview from '@components/ImagePreview'
import MainLayout from '@layouts/MainLayout'
import uuid from 'react-native-uuid'
import api from '@lib/api'
import useSentry from '@hooks/useSentry'
import { useSnackbar, SnackbarThemes } from '@context/SnackbarContext'
import { useCurrentUser } from '@context/CurrentUserContext'

const COPY = i18n.en.WORK_ORDERS.CREATE.ADD_PHOTOS

interface PhotoPayload {
  id: string
  base64: string
  fileExtension: string
}

interface PhotoFile {
  uri: string
  name: string
}

export default function ({ onNext, onPrevious, context }: DefaultStepProps) {
  const [selectedPhoto, setSelectedPhoto] = useState<any>(null)
  const [photos, setPhotos] = useState<Photo[]>(context.photos!.reverse())
  const [photoToUpload, setPhotoToUpload] = useState<PhotoPayload | null>(null)
  const [deletedPhoto, setDeletedPhoto] = useState<string | null>(null)
  const mounted = useRef<boolean>(true)
  const goToNextStep = () => {
    onNext(photos)
  }

  const { captureException } = useSentry()
  const { setSnackbarMessage } = useSnackbar()
  const { currentUser } = useCurrentUser()

  useEffect(() => {
    return () => { mounted.current = false }
  }, [])
  /**
   * Handles pgo upload after re-render once photoToUpload has been updated
   */
  useEffect(() => {
    runAsync(async () => {
      if (photoToUpload === null) return

      try {
        const fileUpload = await api.uploadPhoto(photoToUpload)
        // If the upload succeeds, set fileURL. This will stop showing the image as loading.
        const photoIndex = photos.findIndex((p) => p.id === photoToUpload.id)
        const updatedPhotos = [...photos]
        updatedPhotos[photoIndex].fileURL = fileUpload.fileURL
        if (mounted.current) setPhotos(updatedPhotos)
      } catch (error) {
        // If the upload was not successful, remove the photo from the thumbnails array
        const photoIndex = photos.findIndex((p) => p.id === photoToUpload.id)
        if (mounted.current) {
          setPhotos([
            ...photos.slice(0, photoIndex),
            ...photos.slice(photoIndex + 1, photos.length),
          ])
        }
        captureException(error)
        setSnackbarMessage('Error uploading photo', SnackbarThemes.error)
      }

      // Finally, set photoToUpload to null
      setPhotoToUpload(null)
    })
  }, [photoToUpload])

  async function selectPicture () {
    let originalPhoto
    try {
      originalPhoto = await DocumentPicker.getDocumentAsync({
        type: 'image/*',
      }) as PhotoFile
    } catch (error) {
      captureException(error)
      setSnackbarMessage('Failed to select photo', SnackbarThemes.error)
    }

    if (!originalPhoto) return
    let photo: Maybe<Photo>
    try {
      photo = await ImageManipulator.manipulateAsync(
        originalPhoto.uri,
        [{ resize: { width: 500 } }],
        { base64: true },
      )
    } catch (error) {
      captureException(error)
      setSnackbarMessage('Failed to resize photo', SnackbarThemes.error)
    }

    if (!photo) return
    // The id needs to be set before calling the api
    photo.id = uuid.v4() as string

    // Push new photo to photos array.
    // Since it doesn't have a fileUrl yet, it will be shown as loading.
    setPhotos([...photos, photo])
    setPhotoToUpload({
      id: photo.id,
      fileExtension: originalPhoto.name.split('.').pop() as string,
      base64: photo.base64 as string,
    })
  }

  function updatePhoto (photo: Photo) {
    setPhotos([
      ...photos.filter((p) => p.id !== photo.id),
      photo,
    ])
  }

  async function deletePhoto (fileURL: string) {
    setDeletedPhoto(fileURL)
    const key = fileURL.split('/').pop()
    try {
      const result = await api.deletePhoto(key as string)
      if (!mounted.current) return
      if (result.status === 200) {
        const index = photos.findIndex((p) => p.fileURL === fileURL)
        setPhotos([
          ...photos.slice(0, index),
          ...photos.slice(index + 1, photos.length),
        ])
        setSnackbarMessage('Photo deleted successfully', SnackbarThemes.success)
      }
    } catch (error) {
      captureException(error)
      setSnackbarMessage('Error deleting photo', SnackbarThemes.error)
    } finally {
      if (mounted.current) setDeletedPhoto(null)
    }
  }

  async function createDeletePhotoAlert (fileURL?: string) {
    if (!fileURL) return
    const confirmation = window.confirm('Are you sure you want to delete this photo?')
    if (confirmation) {
      await deletePhoto(fileURL)
    }
  }

  return (
    <MainLayout
      hideBackButton={!!currentUser?.jwtToken}
      onNavigateBack={onPrevious}
    >
      <ScrollContainer>
        <View style={{ width: '100%', flex: 1 }}>
          <View style={{ flexGrow: 1 }}>
            <Header>{COPY.HEADER}</Header>
            <StepDescription>{COPY.DESCRIPTION}</StepDescription>

            <Pressable onPress={selectPicture} disabled={!!photoToUpload}>
              <Card style={styles.card}>
                <Image source={require('@assets/camera.png')} style={styles.cameraIcon} />
                <Text style={styles.cardText}>{COPY.CHOOSE_A_PHOTO}</Text>
              </Card>
            </Pressable>

            <Show when={!!photos.length}>
              <View style={{ height: 160, width: '100%' }}>
                <ScrollView style={styles.capturedPhotos} horizontal={true}>
                  {[...photos]?.reverse().map((photo) => (
                    <ImageThumbnail
                      key={`image-${photo.id}`}
                      photo={photo}
                      isDeleting={photo.fileURL === deletedPhoto}
                      onPress={() => setSelectedPhoto(photo)}
                      onDelete={() => createDeletePhotoAlert(photo.fileURL)}
                    />
                  ))}
                </ScrollView>
                <Text style={styles.tapInstructions}>{COPY.CLICK_INSTRUCTIONS}</Text>
              </View>
            </Show>
          </View>

          <Button
            onPress={goToNextStep}
            disabled={!photos.length || !!photoToUpload}
            label={COPY.NEXT_STEP}
            buttonStyles={{ backgroundColor: colors.blue['400'], ...styles.button }}
            textStyles={{ color: colors.white }}
          />

          <Show when={!!selectedPhoto}>
            <ImagePreview
              photo={selectedPhoto}
              onUpdate={updatePhoto}
              onClose={() => setSelectedPhoto(null)}
            />
          </Show>
        </View>
      </ScrollContainer>
    </MainLayout>
  )
}

const styles = StyleSheet.create({
  card: {
    marginTop: 20,
    alignItems: 'center',
    display: 'flex',
    paddingTop: 30,
    paddingBottom: 30,
  },
  button: {
    marginTop: 20,
  },
  cardText: {
    textTransform: 'uppercase',
    fontWeight: 'bold',
    color: colors.gray['700'],
    textAlign: 'center',
  },
  capturedPhotos: {
    backgroundColor: colors.gray['100'],
    borderRadius: 3,
    padding: 10,
    marginTop: 20,
  },
  cameraIcon: {
    height: 200,
    width: 200,
  },
  photo: {
    height: '100%',
    width: 100,
    marginRight: 10,
    borderRadius: 3,
  },
  tapInstructions: {
    color: colors.gray[300],
    textAlign: 'center',
    marginTop: 5,
  },
})
